import { useState } from 'react';
import formatDate from '../../../helpers/format-date';
import IconUp from '../../icons/up';
import IconDown from '../../icons/down';
import ToggleInput from '../../checkbox/toggle';
import { BlacklistUserInfoAdditionalSegmentRecord } from './types';
import { AdditionalSegmentField } from '../types';

import cn from './blacklist.module.css';

const AdditionalSegmentRecordFullView = ({
  fields,
  title,
  keyValue,
}: {
  fields: AdditionalSegmentField[];
  title: string;
  keyValue: string;
}) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className={cn.recordItemFullView}>
      <button
        type="button"
        className={`${cn.recordItemFullViewTitle} ${
          expanded ? cn.withMargin : ''
        }`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className={cn.recordItemFullViewTitleText}>{title}</div>
        <div className={cn.recordItemFullViewTitleIcon}>
          {expanded ? <IconUp /> : <IconDown />}
        </div>
      </button>
      {expanded && (
        <div
          className={`${cn.recordItemFullViewValues} ${
            title === 'Metadata' ? cn.withoutFlex : ''
          }`}
        >
          {fields?.map((item) => (
            <div className={cn.recordItemFullViewValueWrapper} key={item.id}>
              <div className={cn.recordItemFullViewValueTitle}>
                {/* eslint-disable-next-line no-nested-ternary */}
                {item.derivedName !== 'DocumentofComplianceDOCCompanyCode' &&
                item.derivedName !== 'DOCCountryOfControl' &&
                item.derivedName !== 'DOCCountryOfRegistration'
                  ? item.derivedName === keyValue
                    ? 'Name'
                    : item.derivedName
                        ?.replace(keyValue, '')
                        ?.replace(/(?!^)([A-Z])/g, ' $1')
                  : ''}
                {item.derivedName === 'DocumentofComplianceDOCCompanyCode'
                  ? 'Company Code'
                  : ''}
                {item.derivedName === 'DOCCountryOfControl'
                  ? 'Country Of Control'
                  : ''}
                {item.derivedName === 'DOCCountryOfRegistration'
                  ? 'Country Of Registration'
                  : ''}
              </div>
              <div
                className={`${cn.recordItemFullViewValueText} ${
                  title === 'Metadata' ? cn.fullWidth : ''
                }`}
              >
                {item.derivedName === 'LastUpdateDate'
                  ? formatDate(item?.derivedValue)
                  : item.derivedValue}
                {!item.derivedValue && '—'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const identificationFieldsKeys = [
  'Vessel Type',
  'Gross Tonnage',
  'Country of Build',
  'Year of Build',
  'Yard Number',
  'Ship Status',
  'Deadweight',
  'Port of Registry',
  'ExName',
  'FlagEffectiveDate',
  'ShipStatusEffectiveDate',
];

const beneficialOwnerKeys = [
  'GroupBeneficialOwner',
  'GroupBeneficialOwnerCompanyCode',
  'GroupBeneficialOwnerCountryOfControl',
  'GroupBeneficialOwnerCountryofDomicile',
  'GroupBeneficialOwnerCountryOfReg',
];

const operatorKeys = [
  'Operator',
  'OperatorCompanyCode',
  'OperatorCountryOfControl',
  'OperatorCountryofDomicileName',
  'OperatorCountryOfRegistration',
];

const registeredOwnerKeys = [
  'RegisteredOwner',
  'RegisteredOwnerCode',
  'RegisteredOwnerCountryOfControl',
  'RegisteredOwnerCountryofDomicile',
  'RegisteredOwnerCountryOfRegistration',
];

const shipManagerKeys = [
  'ShipManager',
  'ShipManagerCompanyCode',
  'ShipManagerCountryOfControl',
  'ShipManagerCountryofDomicileName',
  'ShipManagerCountryOfRegistration',
];

const technicalManagerKeys = [
  'TechnicalManager',
  'TechnicalManagerCode',
  'TechnicalManagerCountryOfControl',
  'TechnicalManagerCountryOfDomicile',
  'TechnicalManagerCountryOfRegistration',
];

const docCompanyKeys = [
  'DOCCompany',
  'DocumentofComplianceDOCCompanyCode',
  'DOCCountryOfControl',
  'DOCCompanyCountryofDomicile',
  'DOCCountryOfRegistration',
];

const shipbuilderKeys = [
  'Shipbuilder',
  'ShipbuilderSubContractorShipyardYardHul',
  'ShipbuilderSubContractor',
];

const AdditionalSegmentRecord = ({
  record,
  index,
  segmentId,
}: BlacklistUserInfoAdditionalSegmentRecord) => {
  const [fullView, setFullView] = useState(false);
  const [expanded, setExpanded] = useState(true);

  let content;

  const identificationFields: AdditionalSegmentField[] = [];
  const beneficialOwnerFields: AdditionalSegmentField[] = [];
  const operatorFields: AdditionalSegmentField[] = [];
  const registeredOwnerFields: AdditionalSegmentField[] = [];
  const shipManagerFields: AdditionalSegmentField[] = [];
  const technicalManagerFields: AdditionalSegmentField[] = [];
  const docCompanyFields: AdditionalSegmentField[] = [];
  const shipbuilderFields: AdditionalSegmentField[] = [];
  const otherFields: AdditionalSegmentField[] = [];

  record?.additionalSegmentFields?.forEach((item) => {
    if (identificationFieldsKeys?.includes(item.derivedName)) {
      identificationFields.push(item);
    } else if (beneficialOwnerKeys?.includes(item.derivedName)) {
      beneficialOwnerFields.push(item);
    } else if (operatorKeys?.includes(item.derivedName)) {
      operatorFields.push(item);
    } else if (registeredOwnerKeys?.includes(item.derivedName)) {
      registeredOwnerFields.push(item);
    } else if (shipManagerKeys?.includes(item.derivedName)) {
      shipManagerFields.push(item);
    } else if (technicalManagerKeys?.includes(item.derivedName)) {
      technicalManagerFields.push(item);
    } else if (docCompanyKeys?.includes(item.derivedName)) {
      docCompanyFields.push(item);
    } else if (shipbuilderKeys?.includes(item.derivedName)) {
      shipbuilderFields.push(item);
    } else {
      otherFields.push(item);
    }
  });

  if (!fullView) {
    content = (
      <>
        {identificationFields?.length > 0 && (
          <div className={cn.recordItemDefault}>
            <div className={cn.recordItemDefaultTitle}>
              Identification & Characteristics
            </div>
            <div className={cn.recordItemDefaultValue}>
              {identificationFields?.map((item) => (
                <div className={cn.recordItemDefaultValueItem} key={item.id}>
                  {item.derivedValue}
                </div>
              ))}
            </div>
          </div>
        )}
        {beneficialOwnerFields?.length > 0 && (
          <div className={cn.recordItemDefault}>
            <div className={cn.recordItemDefaultTitle}>
              Group Beneficial Owner
            </div>
            <div className={cn.recordItemDefaultValue}>
              {beneficialOwnerFields?.map((item) => (
                <div className={cn.recordItemDefaultValueItem} key={item.id}>
                  {item.derivedValue}
                </div>
              ))}
            </div>
          </div>
        )}
        {operatorFields?.length > 0 && (
          <div className={cn.recordItemDefault}>
            <div className={cn.recordItemDefaultTitle}>Operator</div>
            <div className={cn.recordItemDefaultValue}>
              {operatorFields?.map((item) => (
                <div className={cn.recordItemDefaultValueItem} key={item.id}>
                  {item.derivedValue}
                </div>
              ))}
            </div>
          </div>
        )}
        {registeredOwnerFields?.length > 0 && (
          <div className={cn.recordItemDefault}>
            <div className={cn.recordItemDefaultTitle}>Registered Owner</div>
            <div className={cn.recordItemDefaultValue}>
              {registeredOwnerFields?.map((item) => (
                <div className={cn.recordItemDefaultValueItem} key={item.id}>
                  {item.derivedValue}
                </div>
              ))}
            </div>
          </div>
        )}
        {shipManagerFields?.length > 0 && (
          <div className={cn.recordItemDefault}>
            <div className={cn.recordItemDefaultTitle}>Ship Manager</div>
            <div className={cn.recordItemDefaultValue}>
              {shipManagerFields?.map((item) => (
                <div className={cn.recordItemDefaultValueItem} key={item.id}>
                  {item.derivedValue}
                </div>
              ))}
            </div>
          </div>
        )}
        {technicalManagerFields?.length > 0 && (
          <div className={cn.recordItemDefault}>
            <div className={cn.recordItemDefaultTitle}>Technical Manager</div>
            <div className={cn.recordItemDefaultValue}>
              {technicalManagerFields?.map((item) => (
                <div className={cn.recordItemDefaultValueItem} key={item.id}>
                  {item.derivedValue}
                </div>
              ))}
            </div>
          </div>
        )}
        {docCompanyFields?.length > 0 && (
          <div className={cn.recordItemDefault}>
            <div className={cn.recordItemDefaultTitle}>DOC Company</div>
            <div className={cn.recordItemDefaultValue}>
              {docCompanyFields?.map((item) => (
                <div className={cn.recordItemDefaultValueItem} key={item.id}>
                  {item.derivedValue}
                </div>
              ))}
            </div>
          </div>
        )}
        {shipbuilderFields?.length > 0 && (
          <div className={cn.recordItemDefault}>
            <div className={cn.recordItemDefaultTitle}>Shipbuilder</div>
            <div className={cn.recordItemDefaultValue}>
              {shipbuilderFields?.map((item) => (
                <div className={cn.recordItemDefaultValueItem} key={item.id}>
                  {item.derivedValue}
                </div>
              ))}
            </div>
          </div>
        )}
        {otherFields?.length > 0 && (
          <div className={cn.recordItemDefault}>
            <div className={cn.recordItemDefaultTitle}>Metadata</div>
            <div className={cn.recordItemDefaultValue}>
              {otherFields?.map((item) => (
                <div className={cn.recordItemDefaultValueItem} key={item.id}>
                  {item.derivedValue}
                </div>
              ))}
            </div>
          </div>
        )}
      </>
    );
  } else {
    content = (
      <>
        {identificationFields?.length > 0 && (
          <AdditionalSegmentRecordFullView
            title="Identification & Characteristics"
            fields={identificationFields}
            keyValue=""
          />
        )}
        {beneficialOwnerFields?.length > 0 && (
          <AdditionalSegmentRecordFullView
            title="Group Beneficial Owner"
            fields={beneficialOwnerFields}
            keyValue="GroupBeneficialOwner"
          />
        )}
        {operatorFields?.length > 0 && (
          <AdditionalSegmentRecordFullView
            title="Operator"
            fields={operatorFields}
            keyValue="Operator"
          />
        )}
        {registeredOwnerFields?.length > 0 && (
          <AdditionalSegmentRecordFullView
            title="Registered Owner"
            fields={registeredOwnerFields}
            keyValue="RegisteredOwner"
          />
        )}
        {shipManagerFields?.length > 0 && (
          <AdditionalSegmentRecordFullView
            title="Ship Manager"
            fields={shipManagerFields}
            keyValue="ShipManager"
          />
        )}
        {technicalManagerFields?.length > 0 && (
          <AdditionalSegmentRecordFullView
            title="Technical Manager"
            fields={technicalManagerFields}
            keyValue="TechnicalManager"
          />
        )}
        {docCompanyFields?.length > 0 && (
          <AdditionalSegmentRecordFullView
            title="DOC Company"
            fields={docCompanyFields}
            keyValue="DOCCompany"
          />
        )}
        {shipbuilderFields?.length > 0 && (
          <AdditionalSegmentRecordFullView
            title="Shipbuilder"
            fields={shipbuilderFields}
            keyValue="Shipbuilder"
          />
        )}
        {otherFields?.length > 0 && (
          <AdditionalSegmentRecordFullView
            title="Metadata"
            fields={otherFields}
            keyValue=""
          />
        )}
      </>
    );
  }

  return (
    <div className={cn.additionalSegmentRecordWrapper}>
      <div className={cn.additionalSegmentRecordTop}>
        <button
          className={cn.additionalSegmentRecordTitle}
          type="button"
          onClick={() => setExpanded(!expanded)}
        >
          <div
            className={cn.additionalSegmentRecordTitleText}
          >{`Record #${index}`}</div>
          <div className={cn.additionalSegmentRecordTitleIcon}>
            {expanded ? (
              <IconUp width={14} height={14} />
            ) : (
              <IconDown width={14} height={14} />
            )}
          </div>
        </button>
        <div className={cn.fullViewWrapper}>
          <ToggleInput
            name={`fullView-${record?.segmentGUID}-${index}-${segmentId}`}
            checked={fullView}
            onChange={(e) => setFullView(e.target.checked)}
            title="Full view"
          />
        </div>
      </div>
      {expanded && content}
    </div>
  );
};

export default AdditionalSegmentRecord;
